import type { SxProps, Theme } from '@mui/material';
import type { FooterBlockType } from '@packages/modules/src/Footer/types';
import type {
  AccordionType,
  CarouselType,
  CmsImageType,
  CmsInstallmentCalculatorType,
  DynamicYieldType,
  ExternalScriptType,
  FlexibleTeaserType,
  GroupType,
  HeadlineType,
  HtmlEditorType,
  HtmlTableEditorType,
  LinkByButtonType,
  LinkByTextType,
  ModalByButtonType,
  ModalByTextlinkType,
  ModalType,
  NewsletterSubscribeFormType,
  NewsletterUnsubscribeFormType,
  NewsletterLegacyUnsubscribeFormType,
  PlaceholderType,
  RecommendationType,
  SectionSeperatorType,
  VideoType,
  VoucherType,
} from '../src/components';
import type { JsonApiParagraphMeta } from './jsonapi';
import type { PromotionBannerType } from './promotion';
import type { TaxonomyTerm, TaxonomyTermMeta } from './taxonomy';
import type { WebappJsonParagraphMeta } from './webapp_json';

/** Defines the types (components) that are possible for usage */
export enum ComponentTypes {
  Accordion = 'accordion',
  AccordionLocalizable = 'accordion_localizable',
  Button = 'button',
  ButtonModal = 'buttonlink_modal',
  BlockText = 'footer_block_text',
  BlockList = 'footer_block_list',
  BlockImage = 'footer_block_image',
  BlockBadges = 'footer_block_badges',
  DynamicYield = 'dynamic_yield',
  ExternalScript = 'externalscript',
  ExternalScriptLocalizable = 'externalscript_localizable',
  ExternalScriptLoadbee = 'external_script_loadbee',
  ExternalScriptHappyContest = 'external_script_happycontest',
  FlexibleTeaser = 'flexible_teaser',
  Footer = 'footer',
  FooterGroupImage = 'footer_group_image',
  FromLibrary = 'from_library',
  Group = 'group',
  GroupLocalizable = 'group_localizable',
  Headline = 'headline',
  Html = 'html',
  HtmlParagraph = 'paragraph--html',
  Image = 'image',
  ImageGroup = 'imagegroup',
  ImageFooter = 'image_footer',
  ImageSimple = 'image_simple',
  ImageSimpleParagraph = 'paragraph--image_simple',
  InstallmentCalculator = 'installment_calculator',
  LinkByButton = 'link_by_button',
  LinkByText = 'link_by_text',
  List = 'list',
  Liveshopping = 'video_liveshopping',
  Newsletter = 'newsletter',
  NewsletterSubscribe = 'subscribe',
  NewsletterUnsubscribe = 'newsletter_unsubscribe',
  ParagraphHtml = 'paragraph--html',
  Placeholder = 'placeholder',
  RecoMagellan = 'reco_magellan',
  RecoPrudsys = 'reco_prudsys',
  SectionSeperator = 'section_seperator',
  Slider = 'slider',
  Socket = 'socket',
  SvgFile = 'svg_file',
  Teaser = 'teaser',
  Text = 'text',
  TextlinkModal = 'textlink_modal',
  VoucherList = 'promotion_list',
  Video = 'video_youtube',
}

export type Languages = 'en' | 'de' | 'fr';

export type TextAlignment = 'center' | 'left' | 'right';

export type Alignment =
  | 'space-between'
  | 'left'
  | 'center'
  | 'right'
  | 'stretch'
  | 'flex-start'
  | 'flex-end'
  | 'bottom'
  | 'top';

export type Distance = 'variation-1' | 'fullwidth' | 'default';

export type TimeControl = {
  field_valid_from?: string | null;
  field_valid_to?: string | null;
  field_valid_until?: string | null;
  field_visible_from?: string | null;
  field_visible_until?: string | null;
};

export type MarginVariant = 'none' | 'small' | 'medium' | 'big';

type Behaviors =
  | []
  | {
      paragraph_size: {
        behaviorSize: 'small' | 'medium' | 'large';
        behaviorSizePlacement: string;
      };
    };

export type ParagraphMeta = {
  langcode?: Languages;
  status?: boolean;
  parent_field_name?: string;
  content_translation_changed?: string;
  field_display_app?: boolean;
  field_display_web?: boolean;
  label?: string;
  behavior_settings?: Behaviors;
} & (JsonApiParagraphMeta | WebappJsonParagraphMeta);

export type ParagraphLibraryType = ParagraphMeta & {
  type: 'from_library';
  paragraphs_library_item: (ParagraphMeta & {
    label: string;
    paragraph: ParagraphType[];
  })[];
};

export enum Audiences {
  anonymous = 'anonymous',
  loggedIn = 'logged in',
  bonusProgram = 'bonusProgram',
  creditAffinity = 'creditAffinity',
}
export type Audience = 'anonymous' | 'logged in' | 'bonusProgram' | 'creditAffinity';

/**
 * Taxonomy describing the Audience for a paragraph
 *
 * Each element of the array is an object that extends the TaxonomyTerm type, and:
 * - Has a 'name' property of type Audience.
 * - Has a 'field_name' property with the value 'field_audience'.
 * - includes an array of type TaxonomyTermMeta with the key that is the value of field 'name'
 */
export type AudienceTaxonomy = {
  taxonomy_term?: (TaxonomyTerm & {
    name: Audience;
    field_name: 'field_audience';
  } & {
    [K in Audience]?: TaxonomyTermMeta[];
  })[];
};

export interface LinkData {
  field_link: string | null;
  field_link_anchor?: string | null;
  field_link_highlight?: string | null;
  field_link_searchfilter?: string | null;
  field_link_target?: '_blank' | '_self' | null;
  field_link_title?: string;
  field_persist_target?: boolean;
  field_tracking?: string | null;
  field_bonus_number?: string | null;
}

export type ParagraphType =
  | AccordionType
  | LinkByButtonType
  | LinkByTextType
  | HtmlEditorType
  | CmsImageType
  | CmsInstallmentCalculatorType
  | ModalByTextlinkType
  | ModalType
  | GroupType
  | PromotionBannerType
  | NewsletterUnsubscribeFormType
  | NewsletterLegacyUnsubscribeFormType
  | NewsletterSubscribeFormType
  | FooterBlockType
  | ExternalScriptType
  | HeadlineType
  | CarouselType
  | RecommendationType
  | DynamicYieldType
  | ParagraphLibraryType
  | PlaceholderType
  | SectionSeperatorType
  | FlexibleTeaserType
  | ModalByButtonType
  | VoucherType
  | VideoType
  | HtmlTableEditorType;

export type DrupalIconTypes = 'ArrowLeft' | 'ArrowRight';

export type RecognizerComponentProps<T extends ParagraphType> = {
  data: T;
  sx?: SxProps<Theme>;
};
